// ============================================================
// Abonelik — kurum yöneticisinin kendi aboneliğini gördüğü ekran
//
// Buradaki para, kurumun MinikPanel'e ödediği ücrettir. Velinin
// kuruma ödediği okul ücretiyle karıştırılmasın diye ekranın
// üstünde bu ayrım açıkça yazılıdır.
// ============================================================

import { PAY, card, emptyNote, sectionTitle } from "../components/ui.js";
import { T } from "../domain/context.js";
import { donemAdi, esc, fmtDate, money } from "../lib/fmt.js";
import { yapabilir } from "../domain/auth.js";
import {
  PLANLAR,
  PLAN_SIRASI,
  abonelik,
  abonelikDurumu,
  aylikUcret,
  denemeKalan,
  kullanim,
  limitDurumu,
  plan,
} from "../domain/billing.js";

const DURUM_ETIKET = {
  aktif:  { label: "Aktif",         chip: "bg-teal-soft text-teal" },
  deneme: { label: "Deneme süresi", chip: "bg-sun-soft text-[#B07E14]" },
  askida: { label: "Askıda",        chip: "bg-coral-soft text-coral" },
  iptal:  { label: "İptal edildi",  chip: "bg-coral text-white" },
};

const ALAN_ADI = { sube: "Şube", ogrenci: "Öğrenci", personel: "Personel" };

export function viewAbonelik() {
  const k = T();
  const a = abonelik(k);
  const p = plan(k);
  const ucret = aylikUcret(k);
  const durum = abonelikDurumu(k);
  const kalan = denemeKalan(k);

  if (!a) return emptyNote("Abonelik kaydı bulunamadı.");

  return `
  <p class="text-sm text-inksoft mb-4">
    Bu ekrandaki tutarlar kurumunuzun <strong>MinikPanel'e</strong> ödediği
    yazılım bedelidir. Velilerin kuruma ödediği okul ücreti
    <a href="#/odemeler" class="text-teal font-semibold hover:underline">Ödemeler</a>
    ekranındadır.
  </p>

  ${kalan != null ? `
    <div class="rounded-xl ${kalan <= 7 ? "bg-coral-soft text-coral" : "bg-sun-soft text-[#B07E14]"} text-sm font-semibold px-4 py-3 mb-4">
      Deneme süresi ${kalan > 0 ? `${kalan} gün sonra` : "bugün"} bitiyor
      (${fmtDate(a.trialEndsAt)}). Sonrasında aylık ${money(ucret.net)} ücretlendirilecek.
    </div>` : ""}

  ${durum.gecikmis.length ? `
    <div class="rounded-xl bg-coral-soft text-coral text-sm font-semibold px-4 py-3 mb-4">
      ${durum.gecikmis.length} abonelik faturası gecikmiş — toplam ${money(durum.gecikmisTutar)}.
    </div>` : ""}

  <div class="grid gap-4 lg:grid-cols-2 items-start">
    ${planKarti(k, a, p, ucret)}
    ${kullanimKarti(k)}
    ${ucretKarti(ucret, a)}
    ${faturaKarti(durum)}
    ${planKarsilastirma(p)}
  </div>`;
}

// ---------- Mevcut plan ----------
function planKarti(k, a, p, ucret) {
  const d = DURUM_ETIKET[a.status] ?? DURUM_ETIKET.aktif;

  return card(`
    <div class="p-5">
      ${sectionTitle("Mevcut Plan", `<span class="text-[11px] font-bold rounded-full px-2.5 py-1 ${d.chip}">${d.label}</span>`)}

      <div class="flex items-baseline gap-2">
        <span class="font-display font-extrabold text-3xl text-grape">${esc(p.label)}</span>
        <span class="text-sm text-inksoft">${a.billingCycle === "yillik" ? "yıllık ödeme" : "aylık ödeme"}</span>
      </div>
      <p class="text-xs text-inksoft mt-1">${esc(p.aciklama)}</p>

      <dl class="grid grid-cols-2 gap-x-3 gap-y-2 text-sm mt-4 pt-3 border-t border-line">
        <div><dt class="text-[11px] font-bold text-inksoft uppercase">Aylık</dt>
          <dd class="font-display font-bold">${money(ucret.net)}</dd></div>
        ${ucret.yillik ? `<div><dt class="text-[11px] font-bold text-inksoft uppercase">Yıllık Toplam</dt>
          <dd class="font-display font-bold">${money(ucret.yillikToplam)}</dd></div>` : ""}
        <div><dt class="text-[11px] font-bold text-inksoft uppercase">Müşteri Sayısı</dt>
          <dd>${fmtDate(a.since)} tarihinden beri</dd></div>
        <div><dt class="text-[11px] font-bold text-inksoft uppercase">Yenileme</dt>
          <dd>${fmtDate(a.renewsAt)}</dd></div>
      </dl>

      ${a.billing ? `
        <div class="text-[11px] text-inksoft mt-3 pt-3 border-t border-line">
          <div class="font-semibold text-ink">${esc(a.billing.unvan)}</div>
          <div>${esc(a.billing.vergiNo)} · ${esc(a.billing.eposta)}</div>
          <div>Ödeme yöntemi: ${esc(a.billing.yontem)}</div>
        </div>` : ""}

      ${yapabilir("planDegistir")
        ? `<p class="text-[11px] text-inksoft mt-3">Plan değişikliği sistem operatörü tarafından yapılır.</p>`
        : `<p class="text-[11px] text-inksoft mt-3">Plan değiştirmek için MinikPanel ile iletişime geçin.</p>`}
    </div>`, "min-w-0");
}

// ---------- Kullanım ve limitler ----------
function kullanimKarti(k) {
  const list = limitDurumu(k);
  const u = kullanim(k);

  return card(`
    <div class="p-5">
      ${sectionTitle("Kullanım & Limitler")}
      <p class="text-[11px] text-inksoft mb-3">
        Limit aşımı kaydı engellemez ama plan yükseltmesi gerektirir.
      </p>

      <div class="space-y-3">
        ${list.map(x => {
          const renk = x.asildi ? "coral" : x.yaklasiyor ? "sun" : "teal";
          return `
          <div>
            <div class="flex items-center justify-between gap-2 text-sm">
              <span>${esc(ALAN_ADI[x.alan] ?? x.alan)}</span>
              <span class="shrink-0 font-semibold">
                ${x.olan}${x.sinirsiz ? "" : ` / ${x.limit}`}
                ${x.sinirsiz ? `<span class="text-[11px] text-inksoft font-normal">sınırsız</span>` : ""}
              </span>
            </div>
            ${x.sinirsiz ? "" : `
              <div class="h-1.5 rounded-full bg-line mt-1 overflow-hidden">
                <div class="h-full rounded-full bg-${renk}" style="width:${Math.min(100, x.oran)}%"></div>
              </div>`}
            ${x.asildi ? `<div class="text-[11px] text-coral font-semibold mt-1">Sınır aşıldı — plan yükseltilmeli.</div>` : ""}
          </div>`;
        }).join("")}
      </div>

      <div class="text-[11px] text-inksoft mt-4 pt-3 border-t border-line">
        ${u.sube} şube · ${u.ogrenci} öğrenci · ${u.personel} personel
      </div>
    </div>`, "min-w-0");
}

// ---------- Ücret dökümü ----------
function ucretKarti(u, a) {
  return card(`
    <div class="p-5">
      ${sectionTitle("Ücret Nasıl Hesaplanıyor", `<span class="text-[11px] text-inksoft">${donemAdi(a.since.slice(0, 7) > "2026-08" ? a.since.slice(0, 7) : "2026-08")}</span>`)}

      <div class="space-y-1 text-sm">
        <div class="flex justify-between gap-3">
          <span class="text-inksoft">Plan tabanı</span>
          <span class="shrink-0">${money(u.taban)}</span>
        </div>
        ${u.ekSubeAdet ? `
          <div class="flex justify-between gap-3">
            <span class="text-inksoft">Ek şube (${u.ekSubeAdet} × ${money(u.ekSube / u.ekSubeAdet)})</span>
            <span class="shrink-0">${money(u.ekSube)}</span>
          </div>` : ""}
        <div class="flex justify-between gap-3">
          <span class="text-inksoft">Öğrenci (${u.ogrenciAdet} × ${money(u.ogrenci / (u.ogrenciAdet || 1))})</span>
          <span class="shrink-0">${money(u.ogrenci)}</span>
        </div>
        ${u.donemIndirimPct ? `
          <div class="flex justify-between gap-3 text-teal">
            <span>Yıllık ödeme indirimi (%${u.donemIndirimPct})</span>
            <span class="shrink-0">− ${money(Math.round(u.brut * u.donemIndirimPct / 100))}</span>
          </div>` : ""}
        ${u.musteriIndirimPct ? `
          <div class="flex justify-between gap-3 text-teal">
            <span>${esc(a.discountNote ?? "Müşteri indirimi")} (%${u.musteriIndirimPct})</span>
            <span class="shrink-0">− ${money(Math.round(u.brut * u.musteriIndirimPct / 100))}</span>
          </div>` : ""}
      </div>

      <div class="flex justify-between font-display font-bold text-lg mt-3 pt-3 border-t border-line">
        <span>Aylık toplam</span><span>${money(u.net)}</span>
      </div>

      <p class="text-[11px] text-inksoft mt-2">
        Tutar fatura kaydında saklanmaz; plan tarifesinden ve o dönemin
        kullanımından hesaplanır. Şube veya öğrenci sayısı değişince
        bir sonraki dönem kendiliğinden güncellenir.
      </p>
    </div>`, "min-w-0");
}

// ---------- Fatura geçmişi ----------
function faturaKarti(durum) {
  return card(`
    <div class="p-5">
      ${sectionTitle("Abonelik Faturaları", `<span class="text-[11px] text-inksoft">${durum.faturalar.length} dönem</span>`)}

      ${durum.acikTutar ? `
        <div class="rounded-xl bg-coral-soft p-3 mb-3 flex items-center justify-between gap-2">
          <span class="text-xs font-semibold text-coral">Açık bakiye</span>
          <span class="font-display font-bold text-coral">${money(durum.acikTutar)}</span>
        </div>` : ""}

      <div class="space-y-1.5 max-h-80 overflow-y-auto pr-1">
        ${durum.faturalar.map(f => {
          const odendi = Boolean(f.paidAt);
          const gecikmis = !odendi && f.dueAt < "2026-08-05";
          const rozet = odendi ? PAY.odendi : gecikmis ? PAY.gecikti : PAY.bekliyor;
          return `
          <div class="rounded-lg border border-line px-3 py-2">
            <div class="flex items-center justify-between gap-2">
              <span class="text-xs font-semibold">${donemAdi(f.period)}</span>
              <span class="flex items-center gap-2 shrink-0">
                <span class="text-xs font-semibold">${money(f.tutar)}</span>
                <span class="text-[9px] font-bold rounded-full px-1.5 py-0.5 ${rozet.chip}">${rozet.label}</span>
              </span>
            </div>
            <div class="text-[10px] text-inksoft mt-1">
              ${odendi
                ? `${fmtDate(f.paidAt)} · ${esc(f.method ?? "—")} · ${esc(f.makbuzNo)}`
                : `Son ödeme ${fmtDate(f.dueAt)}`}
              ${f.note ? ` · ${esc(f.note)}` : ""}
            </div>
          </div>`;
        }).join("")}
      </div>
    </div>`, "min-w-0");
}

// ---------- Plan karşılaştırma ----------
function planKarsilastirma(mevcut) {
  return card(`
    <div class="p-5">
      ${sectionTitle("Planlar")}
      <div class="overflow-x-auto">
        <table class="w-full text-sm min-w-[560px]">
          <thead><tr class="text-left text-[11px] uppercase tracking-wide text-inksoft border-b border-line">
            <th class="py-2 pr-3">Plan</th>
            <th class="py-2 pr-3 text-right">Taban</th>
            <th class="py-2 pr-3 text-right">Öğrenci/ay</th>
            <th class="py-2 pr-3">Limitler</th>
            <th class="py-2">Ek modüller</th>
          </tr></thead>
          <tbody class="divide-y divide-line/70">
            ${PLAN_SIRASI.map((id, i) => {
              const p = PLANLAR[id];
              const onceki = i > 0 ? PLANLAR[PLAN_SIRASI[i - 1]] : null;
              const ek = onceki ? p.moduller.filter(m => !onceki.moduller.includes(m)) : [];
              const aktif = p.id === mevcut.id;
              return `
              <tr class="${aktif ? "bg-paper/60" : ""}">
                <td class="py-2.5 pr-3">
                  <span class="font-semibold">${esc(p.label)}</span>
                  ${aktif ? `<span class="text-[10px] font-bold rounded-full bg-teal-soft text-teal px-2 py-0.5 ml-1">Mevcut</span>` : ""}
                </td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap">${money(p.aylikTaban)}</td>
                <td class="py-2.5 pr-3 text-right whitespace-nowrap">${money(p.ogrenciBasina)}</td>
                <td class="py-2.5 pr-3 text-[11px] text-inksoft whitespace-nowrap">
                  ${p.limitler.sube ?? "∞"} şube · ${p.limitler.ogrenci ?? "∞"} öğrenci
                </td>
                <td class="py-2.5 text-[11px] text-inksoft">
                  ${ek.length ? ek.map(m => esc(MODUL_ADI[m] ?? m)).join(", ") : "—"}
                </td>
              </tr>`;
            }).join("")}
          </tbody>
        </table>
      </div>
    </div>`, "min-w-0 lg:col-span-2");
}

const MODUL_ADI = {
  saglik: "Sağlık", basvurular: "Başvurular", finans: "Finans",
  odemeler: "Ödemeler", etkinlikler: "Etkinlikler",
};
